import {
  keepPreviousData,
  useMutation,
  useQueries,
  useQuery
} from "@tanstack/react-query";
import { instance } from "api/apiBase";
import addressApi from "api/landlord/address";
import {
  useMemo,
  useState,
  Dispatch,
  SetStateAction,
  useEffect,
  useCallback
} from "react";
import { toast } from "react-toastify";
import {
  ApplicationStatus,
  PaginatedResponse,
  ScreeningStatus
} from "shared/types";
import { useSearchParamsState } from "shared/hooks/use-search-params-state";
import {
  Button,
  Input,
  InputSize,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  Show,
  IconButtonVariant,
  Select
} from "shared/components";
import { FilterLines, Plus, XClose } from "untitledui-js-base";
import { ApplicationsDataGridTable } from "./components/Table/ApplicationsDataGridTable";
import { Pagination } from "shared/components/Pagination";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";
import { Application } from "types/application";
import { RowSelectionState, SortingState } from "@tanstack/react-table";
import { MergeApplicantsModal } from "./components/MergeApplicantsModal";
import { CancelOrRefundModal } from "../CancelOrRefundModal";
import queryString from "qs";
import { MoveModal } from "../MoveModal";
import { FiltersDrawer } from "./components/FiltersDrawers";
import { FilterBadge } from "./components/FiltersDrawers/FilterBadge";
import { Tabs } from "../../components/Tabs";
import { RefundModal } from "../RefundModal";
import { useMedia } from "shared/providers";
import { CountNotification } from "shared/app/ApplicationsTableView/components/CountNotification";
import { InviteAndCreateModal } from "../InviteAndCreateModal";
import { UncancelModal } from "../UncancelModal";
import { ColumnsMenu } from "./components/ColumnsMenu";
import { getErrorMessage } from "utils/error";
import { DEFAULT_COLUMN_VISIBILITY, sortableKeys } from "./constants";
import { useAuthContext } from "providers/Auth";
import { SplitModal } from "../SplitModal";
import { ActiveTab } from "./components/Table/constants";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";

const query = "/portal/v1/applications";

export interface ApplicationFilters {
  status?: ApplicationStatus[];
  properties?: { id: string; name: string }[];
  cities?: string[];
  units?: { id: string; name: string }[];
  screeningStatuses?: ScreeningStatus[];
}

const removePropertyFilter = (
  prev: ApplicationFilters | undefined,
  propertyId: string
) => {
  if (!prev || !prev.properties?.length) return prev;

  const nextProperties: { id: string; name: string }[] = [];

  for (const property of prev.properties)
    if (property.id !== propertyId) nextProperties.push(property);

  if (nextProperties.length === prev.properties.length) return prev;

  return {
    ...prev,
    properties: nextProperties
  };
};

const removeCityFilter = (
  prev: ApplicationFilters | undefined,
  city: string
) => {
  if (!prev || !prev.cities?.length) return prev;

  const nextCities: string[] = [];

  for (const currentCity of prev.cities)
    if (currentCity !== city) nextCities.push(currentCity);

  if (nextCities.length === prev.cities.length) return prev;

  return {
    ...prev,
    cities: nextCities
  };
};

const removeUnitFilter = (
  prev: ApplicationFilters | undefined,
  unitId: string
) => {
  if (!prev || !prev.units?.length) return prev;

  const nextUnits: { id: string; name: string }[] = [];

  for (const unit of prev.units) if (unit.id !== unitId) nextUnits.push(unit);

  if (nextUnits.length === prev.units.length) return prev;

  return {
    ...prev,
    units: nextUnits
  };
};

const removeScreeningStatusFilter = (
  prev: ApplicationFilters | undefined,
  status: ScreeningStatus
) => {
  if (!prev || !prev.screeningStatuses?.length) return prev;

  const nextStatuses: ScreeningStatus[] = [];

  for (const currentStatus of prev.screeningStatuses)
    if (currentStatus !== status) nextStatuses.push(currentStatus);

  if (nextStatuses.length === prev.screeningStatuses.length) return prev;

  return {
    ...prev,
    screeningStatuses: nextStatuses
  };
};

const DEFAULT_PER_PAGE = window.localStorage.getItem("per_page") || "10";

export const ApplicationsTableView = ({
  propertyId,
  unitId
}: {
  propertyId?: number;
  unitId?: number;
}) => {
  const { isMobile } = useMedia();
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [selectedRentersRow, setSelectedRentersRow] =
    useState<RowSelectionState>({});
  const [sorting, setSorting] = useState<SortingState>([]);
  const [groupBy, setGroupBy] = useState<string>();

  const handleSortingChange: Dispatch<SetStateAction<SortingState>> = (
    newSorting
  ) => {
    if (typeof newSorting === "function")
      setSorting((prev) => {
        const next = newSorting(prev).slice(-1);
        return next;
      });
    else {
      const next = newSorting.slice(-1);
      setSorting(next);
    }
  };
  const { auth, refreshAuthUserDashboardState } = useAuthContext();

  const [cancelModalId, setCancelModalId] = useState<string | undefined>();
  const [uncancelModalId, setUncancelModalId] = useState<string | undefined>();
  const [moveModalId, setMoveModalId] = useState<string | undefined>();
  const [splitModalId, setSplitModalId] = useState<string | undefined>();
  const [isShowModal, setIsShowModal] = useState(false);

  const dictionary = useUnitsPropertiesDictionary();

  const [searchParams, setSearchParams] = useSearchParamsState(
    {
      application: "",
      page: "1",
      tab: ActiveTab.Submitted,
      per_page: DEFAULT_PER_PAGE
    },
    ["application", "page", "tab", "per_page"]
  );
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<ApplicationFilters>();
  const [refundModalId, setRefundModalId] = useState<string | undefined>();
  const [debouncedParams] = useDebouncedValue(searchParams.application, 500);
  const dataQuery = useQuery<Application>({
    queryKey: ["APPLICATION", cancelModalId],
    queryFn: () => addressApi.getApplicationById(cancelModalId || ""),
    enabled: !!cancelModalId
  });

  const { applicants } = dataQuery.data || {};

  const updateColumnVisibility = useMutation({
    mutationKey: ["updateColumnVisibility"],
    mutationFn: (columnsVisibility: any) =>
      instance.patch(`/portal/v1/users/${auth?.id}`, {
        columns_visibility: columnsVisibility
      })
  });

  const [columnsVisibility, setColumnsVisibility] = useState(
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- tab can be undefined
    auth?.columns_visibility?.[searchParams.tab?.toLowerCase()] ||
      DEFAULT_COLUMN_VISIBILITY
  );
  const [totalColumnsVisibility, setTotalColumnsVisibility] = useState(
    auth?.columns_visibility || {}
  );

  const handleRemoveProperty = useCallback(
    (propertyId: string) => {
      setFilters((prev) => removePropertyFilter(prev, propertyId));
    },
    [setFilters]
  );

  const handleRemoveCity = useCallback(
    (city: string) => {
      setFilters((prev) => removeCityFilter(prev, city));
    },
    [setFilters]
  );

  const handleRemoveUnit = useCallback(
    (unitId: string) => {
      setFilters((prev) => removeUnitFilter(prev, unitId));
    },
    [setFilters]
  );

  const handleRemoveScreeningStatus = useCallback(
    (status: ScreeningStatus) => {
      setFilters((prev) => removeScreeningStatusFilter(prev, status));
    },
    [setFilters]
  );

  useEffect(() => {
    setColumnsVisibility(
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      totalColumnsVisibility[searchParams.tab?.toLowerCase()] ||
        DEFAULT_COLUMN_VISIBILITY
    );
  }, [searchParams.tab]);

  const handleColumnToggle = async (id: string, checked: boolean) => {
    const updateColumnsVisibility = columnsVisibility.map((col) =>
      col.id === id ? { ...col, checked } : col
    );
    setColumnsVisibility(updateColumnsVisibility);

    const updatedColumnsVisibility = {
      ...totalColumnsVisibility,
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- tab can be undefined
      [searchParams.tab?.toLowerCase()]: updateColumnsVisibility
    };
    setTotalColumnsVisibility(updatedColumnsVisibility);

    try {
      await updateColumnVisibility
        .mutateAsync(updatedColumnsVisibility)
        .then(() => {
          refreshAuthUserDashboardState();
        });
    } catch (error) {
      toast.error(
        `Failed to update column visibility: ${getErrorMessage(error)}`
      );
    }
  };
  const getApplications = async (
    status?: ApplicationStatus[],
    perPage?: number
  ) => {
    const sorts = sorting[0]
      ? [{ [sorting[0].id]: sorting[0].desc ? "desc" : "asc" }]
      : [];
    return instance
      .get(
        query +
          "?" +
          queryString.stringify(
            {
              per_page: perPage || searchParams.per_page || DEFAULT_PER_PAGE,
              page: searchParams.page || "1",
              search: debouncedParams.trim(),
              status,
              property_ids: filters?.properties?.map((p) => p.id),
              unit_ids: filters?.units?.map((u) => u.id),
              cities: filters?.cities,
              screening_statuses: filters?.screeningStatuses,
              property_id: propertyId,
              unit_id: unitId,
              sorts,
              group_by: groupBy || undefined
            },
            {
              arrayFormat: "brackets"
            }
          )
      )
      .then(({ data }: { data: PaginatedResponse<Application> }) => data);
  };

  const buildQuery = (status: ApplicationStatus) => {
    return {
      queryKey: [
        query + "__total",
        {
          page: 1,
          per_page: 1,
          search: debouncedParams.trim() || undefined,
          status: status,
          property_ids: filters?.properties?.map((p) => p.id),
          unit_ids: filters?.units?.map((u) => u.id),
          cities: filters?.cities,
          screening_statuses: filters?.screeningStatuses,
          property_id: propertyId,
          unit_id: unitId
        }
      ],
      queryFn: () => getApplications([status], 1).then((d) => d.total_count),
      onSuccess: (data) => {
        if (
          status === ApplicationStatus.Submitted &&
          data.total_count > 0 &&
          !searchParams.tab
        )
          setSearchParams({ ...searchParams, tab: ActiveTab.Submitted });
      }
    };
  };

  const [
    { data: totalSubmittedCount },
    { data: totalPartiallySubmittedCount }
  ] = useQueries({
    queries: [
      buildQuery(ApplicationStatus.Submitted),
      buildQuery(ApplicationStatus.PartiallySubmitted)
    ]
  });

  const status = useMemo(() => {
    const statusMap = {
      NotSubmitted: [ApplicationStatus.Pending, ApplicationStatus.Started],
      PartiallySubmitted: [ApplicationStatus.PartiallySubmitted],
      Submitted: [ApplicationStatus.Submitted],
      Approved: [ApplicationStatus.Approved],
      ApprovedConditionally: [ApplicationStatus.ConditionalApproved],
      Declined: [ApplicationStatus.Rejected],
      Canceled: [ApplicationStatus.Canceled],
      OnHold: [ApplicationStatus.OnHold]
    };
    return statusMap[searchParams.tab] ?? [];
  }, [searchParams.tab]);

  const { data: applications, isFetching } = useQuery({
    queryKey: [
      "APPLICANTS_TABLE",
      query,
      {
        applications: debouncedParams,
        per_page: searchParams.per_page || DEFAULT_PER_PAGE,
        page: searchParams.page || "1",
        status,
        property_ids: filters?.properties?.map((p) => p.id),
        unit_ids: filters?.units?.map((u) => u.id),
        cities: filters?.cities,
        screening_statuses: filters?.screeningStatuses,
        property_id: propertyId,
        unit_id: unitId,
        sorting,
        group_by: groupBy || undefined
      }
    ],
    queryFn: () => getApplications(status),
    placeholderData: keepPreviousData
  });

  const tableData = useMemo(() => {
    return applications?.items;
  }, [applications?.items]);

  return (
    <>
      <div className="space-y-4">
        <div className="flex w-full justify-between flex-wrap md:flex-nowrap gap-2 md:max-w-[calc(100vw-124px)]">
          <div className="flex w-full items-center gap-2 ">
            <Input
              id="search"
              autoComplete="off"
              placeholder="Search applications"
              value={searchParams.application}
              onChange={(e) =>
                setSearchParams({
                  ...searchParams,
                  page: "1",
                  application: e.target.value
                })
              }
              size={InputSize.SM}
              wrapperClassName="w-full"
              className="!mb-0 md:max-w-[271px]"
            />
          </div>

          {propertyId || unitId ? null : (
            <div className="md:flex w-full justify-end hidden">
              <Button
                variant={ButtonVariant.PRIMARY}
                className="w-full md:w-auto"
                size={ButtonSize.SM}
                Icon={Plus}
                onClick={() => setIsShowModal(true)}
              >
                Create application
              </Button>
            </div>
          )}
        </div>

        <div className="flex gap-2 h-10">
          <Tabs
            tabs={[
              ActiveTab.All,
              ActiveTab.Submitted,
              ActiveTab.PartiallySubmitted,
              ActiveTab.NotSubmitted,
              ActiveTab.OnHold,
              ActiveTab.Approved,
              ActiveTab.ApprovedConditionally,
              ActiveTab.Declined,
              ActiveTab.Canceled
            ]}
            displayMap={{
              All: "All",
              NotSubmitted: "Not submitted",
              PartiallySubmitted: (
                <>
                  Partially submitted
                  <CountNotification
                    totalCount={totalPartiallySubmittedCount}
                  />
                </>
              ),
              Submitted: (
                <>
                  Submitted
                  <CountNotification totalCount={totalSubmittedCount} />
                </>
              ),
              Approved: "Approved",
              ApprovedConditionally: "Approved conditionally",
              Declined: "Declined",
              OnHold: "On hold",
              Canceled: "Canceled"
            }}
            selected={searchParams.tab as ActiveTab}
            setActiveTab={(t: ActiveTab) => {
              setSearchParams({
                tab: t,
                application: searchParams.application,
                page: "1"
              });
            }}
          />
          <Show if={!unitId}>
            <div className="relative">
              <IconButton
                size={isMobile ? IconButtonSize.BASE : IconButtonSize.SM}
                variant={IconButtonVariant.BASE}
                Icon={FilterLines}
                onClick={() => setShowFilters(true)}
              />
              {Object.values(filters ?? {}).some((f) => f?.length) && (
                <div className="p-1 rounded-full bg-white absolute bottom-[5px] right-[4px]">
                  <div className=" size-2 z-20 bg-primary-500 rounded-full" />
                </div>
              )}
            </div>
          </Show>

          <div className="relative">
            <ColumnsMenu
              activeTab={searchParams.tab as ActiveTab}
              isMobile={isMobile}
              columnsData={columnsVisibility}
              onColumnToggle={handleColumnToggle}
            />
          </div>

          <Select
            size={InputSize.SM}
            id="group_by"
            placeholder="Group by"
            className="!mb-0 ml-auto w-[200px]"
            value={groupBy ?? ""}
            onChange={(e) =>
              setGroupBy(e.target.value ? e.target.value : undefined)
            }
            options={[
              {
                label: "Group application",
                value: ""
              },
              {
                label: "Screening status",
                value: "screening_status"
              },
              {
                label: dictionary.Unit,
                value: "unit"
              }
            ]}
          />
        </div>

        <Show if={Object.values(filters ?? {}).some((f) => f?.length)}>
          <div className="flex flex-wrap gap-2">
            {filters?.properties?.map((property) => (
              <FilterBadge
                key={property.id}
                filter={property.name}
                onRemove={() => handleRemoveProperty(property.id)}
              />
            ))}

            {filters?.cities?.map((city) => (
              <FilterBadge
                key={city}
                filter={city}
                onRemove={() => handleRemoveCity(city)}
              />
            ))}

            {filters?.units?.map((unit) => (
              <FilterBadge
                key={unit.id}
                filter={unit.name}
                onRemove={() => handleRemoveUnit(unit.id)}
              />
            ))}

            {filters?.screeningStatuses?.map((status) => (
              <FilterBadge
                key={status}
                filter={status}
                onRemove={() => handleRemoveScreeningStatus(status)}
              />
            ))}
          </div>
        </Show>

        {Object.values(selectedRentersRow).filter(Boolean).length ? (
          <div className="shadow-lg bg-white px-4 py-2 rounded-lg border border-alpha-100 z-[11] absolute right-10 bottom-24 animate-appear flex justify-end items-center gap-2">
            <p className="mr-4 text-sm font-medium text-content-1000 truncate">
              {Object.values(selectedRentersRow).filter(Boolean).length}{" "}
              applications selected
            </p>
            <Button
              size={ButtonSize.SM}
              onClick={() => {
                setShowMergeModal(true);
              }}
              variant={ButtonVariant.PRIMARY}
              disabled={
                Object.values(selectedRentersRow).filter(Boolean).length < 2
              }
            >
              Merge applications
            </Button>
            <IconButton
              size={IconButtonSize.SM}
              Icon={XClose}
              onClick={() => setSelectedRentersRow({})}
            />
          </div>
        ) : null}

        <ApplicationsDataGridTable
          groupBy={groupBy}
          setSorting={handleSortingChange}
          sorting={sorting}
          activeTab={searchParams.tab as ActiveTab}
          sortableKeys={sortableKeys}
          selectedRentersRow={selectedRentersRow as Record<string, boolean>}
          setSelectedRentersRow={setSelectedRentersRow}
          applications={tableData ?? []}
          onInviteClick={() => {
            setIsShowModal(true);
          }}
          perPageRows={Number(DEFAULT_PER_PAGE)}
          isLoading={isFetching}
          onCancelClick={(id) => {
            setCancelModalId(id);
          }}
          onUncancelClick={(id) => {
            setUncancelModalId(id);
          }}
          onRefundClick={(id) => setRefundModalId(id)}
          onSplitClick={(id) => setSplitModalId(id)}
          onMoveClick={(id) => setMoveModalId(id)}
          hasSearchParams={!!debouncedParams}
          columnsVisibility={columnsVisibility}
          invalidateQueries={[
            buildQuery(ApplicationStatus.Submitted).queryKey,
            buildQuery(ApplicationStatus.PartiallySubmitted).queryKey,
            [
              "APPLICANTS_TABLE",
              query,
              {
                applications: debouncedParams,
                per_page: searchParams.per_page || DEFAULT_PER_PAGE,
                page: searchParams.page || "1",
                status,
                property_id: propertyId,
                unit_id: unitId,
                sorting
              }
            ]
          ]}
          pagination={
            <Pagination
              className="justify-end"
              total={applications?.page_count as number}
              current={Number(searchParams.page)}
              onChange={(e) =>
                setSearchParams({ ...searchParams, page: String(e) })
              }
            />
          }
        />
      </div>
      {showMergeModal ? (
        <MergeApplicantsModal
          isOpen={showMergeModal}
          onClose={() => setShowMergeModal(false)}
          selectedApplicants={Object.keys(selectedRentersRow)}
        />
      ) : null}
      {isShowModal && (
        <InviteAndCreateModal
          open={isShowModal}
          setOpen={() => setIsShowModal(false)}
          unitId={unitId}
          propertyId={propertyId}
          onInviteRefreshKey={[
            "APPLICANTS_TABLE",
            query,
            {
              applications: debouncedParams,
              per_page: searchParams.per_page || DEFAULT_PER_PAGE,
              page: searchParams.page || "1",
              status,
              property_ids: filters?.properties?.map((p) => p.id),
              unit_ids: filters?.units?.map((u) => u.id),
              cities: filters?.cities,
              property_id: propertyId,
              unit_id: unitId,
              sorting
            }
          ]}
        />
      )}
      <Show if={Boolean(cancelModalId)}>
        <CancelOrRefundModal
          applicants={applicants}
          applicationId={cancelModalId ?? ""}
          onClose={() => setCancelModalId(undefined)}
        />
      </Show>
      <Show if={Boolean(uncancelModalId)}>
        <UncancelModal
          applicationId={uncancelModalId ?? ""}
          onClose={() => setUncancelModalId(undefined)}
        />
      </Show>
      <Show if={Boolean(splitModalId)}>
        <SplitModal
          isOpen={Boolean(splitModalId)}
          applicationId={splitModalId ?? ""}
          onClose={() => setSplitModalId(undefined)}
        />
      </Show>
      <Show if={Boolean(moveModalId)}>
        <MoveModal
          applicationId={moveModalId ?? ""}
          onClose={() => setMoveModalId(undefined)}
        />
      </Show>
      <Show if={Boolean(refundModalId)}>
        <RefundModal
          queryKey={[
            "APPLICANTS_TABLE",
            query,
            {
              applications: debouncedParams,
              per_page: searchParams.per_page || DEFAULT_PER_PAGE,
              page: searchParams.page || "1",
              status,
              property_ids: filters?.properties?.map((p) => p.id),
              unit_ids: filters?.units?.map((u) => u.id),
              cities: filters?.cities,
              property_id: propertyId,
              unit_id: unitId,
              sorting
            }
          ]}
          applicationId={refundModalId ?? ""}
          onClose={() => setRefundModalId(undefined)}
        />
      </Show>
      <FiltersDrawer
        unitId={unitId}
        propertyId={propertyId}
        filters={filters}
        setFilters={setFilters}
        showFilters={showFilters}
        setShowFilters={setShowFilters}
      />
    </>
  );
};
